import { useMemo } from 'react';
import { zipObject } from 'lodash';
import useIntl from '../../hooks/useIntl';
import rdModalMessages from './RDBudgetModal/messages';
import { formatCurrency } from '@fortress-technology-solutions/fortress-component-library/utils';
import { COMPLIANCE_STATES } from './constants';
import {
  calculateEndingBalanceComplianceState,
  calculateRTOComplianceState,
  calculateSurplusComplianceState,
  hasMissingCommentsOnOtherFields,
} from './utils';
import type { UseFormReturn } from 'react-hook-form';

const dependencyNames = [
  '0-0-6-comments',
  '0-0-6-proposedBudget',
  '0-0-7-comments',
  '0-0-9-proposedBudget',
  '0-2-0-proposedBudget',
  '0-2-1-proposedBudget',
  '0-2-6-proposedBudget',
  '0-2-7-currentBudget',
  '0-2-7-proposedBudget',
  '0-3-0-proposedBudget',
  '0-3-5-proposedBudget',
  '0-4-2-proposedBudget',
  '1-0-10-comments',
  '1-0-10-variancePercentage',
  '1-0-9-comments',
  '1-0-9-proposedBudget',
  '1-1-5-comments',
  '1-1-5-proposedBudget',
  '1-1-6-comments',
  '1-1-6-variancePercentage',
  '1-2-13-comments',
  '1-2-13-proposedBudget',
  '1-2-14-comments',
  '1-2-14-proposedBudget',
  '1-2-14-variancePercentage',
  '1-3-2-comments',
  '1-3-2-proposedBudget',
  '1-3-5-comments',
  '1-3-5-proposedBudget',
  '1-3-6-comments',
  '1-3-6-proposedBudget',
  '1-3-6-variancePercentage',
  '1-3-7-proposedBudget',
  '2-0-0-comments',
  '2-0-6-comments',
  '2-0-6-proposedBudget',
  '2-0-8-proposedBudget',
  '2-bottom-2-Reserve Account Required Balance',
  '2-bottom-4-Insurance Deductible',
  '3-0-4-comments',
  '3-0-4-operatingProposed',
  '3-0-4-reserveProposed',
  '3-1-4-comments',
  '3-1-4-operatingProposed',
  '3-1-4-reserveProposed',
  '3-10-5-comments',
  '3-10-5-operatingProposed',
  '3-10-5-reserveProposed',
  '3-11-1-comments',
  '3-11-1-operatingProposed',
  '3-11-1-reserveProposed',
  '3-12-2-comments',
  '3-12-2-operatingProposed',
  '3-12-2-reserveProposed',
  '3-13-0-comments',
  '3-13-0-operatingProposed',
  '3-13-0-reserveProposed',
  '3-13-1-comments',
  '3-13-1-operatingProposed',
  '3-13-1-reserveProposed',
  '3-13-2-comments',
  '3-13-2-operatingProposed',
  '3-13-2-reserveProposed',
  '3-2-2-comments',
  '3-2-2-operatingProposed',
  '3-2-2-reserveProposed',
  '3-3-2-comments',
  '3-3-2-operatingProposed',
  '3-3-2-reserveProposed',
  '3-4-1-comments',
  '3-4-1-operatingProposed',
  '3-4-1-reserveProposed',
  '3-5-2-comments',
  '3-5-2-operatingProposed',
  '3-5-2-reserveProposed',
  '3-6-5-comments',
  '3-6-5-operatingProposed',
  '3-6-5-reserveProposed',
  '3-7-1-comments',
  '3-7-1-operatingProposed',
  '3-7-1-reserveProposed',
  '3-8-6-comments',
  '3-8-6-operatingProposed',
  '3-8-6-reserveProposed',
  '3-9-3-comments',
  '3-9-3-operatingProposed',
  '3-9-3-reserveProposed',
];

const useRdBudgetCompliance = (
  selectedProperty,
  units,
  useFormReturn: UseFormReturn,
  skipValidation: boolean = false,
) => {
  const intl = useIntl();

  const dependencyValues = useFormReturn.watch(dependencyNames);

  return useMemo(() => {
    if (skipValidation) return [];

    const formValues = zipObject(dependencyNames, dependencyValues);

    const netCash = formValues['0-3-5-proposedBudget'] || 0;
    const vacancyRate = parseFloat(formValues['0-0-7-comments'] || '0%');
    const partILine10ProposedValue =
      parseFloat(formValues['0-0-9-proposedBudget']) || 0;
    const partILine16ProposedValue =
      parseFloat(formValues['0-2-0-proposedBudget']) || 0;
    const partILine17ProposedValue =
      parseFloat(formValues['0-2-1-proposedBudget']) || 0;
    const partILine22ProposedValue =
      parseFloat(formValues['0-2-6-proposedBudget']) || 0;
    const partILine23CurrentValue =
      parseFloat(formValues['0-2-7-currentBudget']) || 0;
    const partILine23ProposedValue =
      parseFloat(formValues['0-2-7-proposedBudget']) || 0;
    const partILine25ProposedValue =
      parseFloat(formValues['0-3-0-proposedBudget']) || 0;
    const partILine33ProposedValue =
      parseFloat(formValues['0-4-2-proposedBudget']) || 0;

    const partIILine33ProposedValue =
      parseFloat(formValues['1-2-14-proposedBudget']) || 0;
    const partIILine33Comments = formValues['1-2-14-comments'] || '';
    const partIILine40ProposedValue =
      parseFloat(formValues['1-3-6-proposedBudget']) || 0;
    const partIILine41ProposedValue =
      parseFloat(formValues['1-3-7-proposedBudget']) || 0;
    const partIIILine8ProposedValue =
      parseFloat(formValues['2-0-8-proposedBudget']) || 0;
    const partIIIReservedMaxAmount =
      parseFloat(formValues['2-bottom-2-Reserve Account Required Balance']) ||
      0;
    const partIIIInsuranceDeductibleAmount =
      parseFloat(formValues['2-bottom-4-Insurance Deductible']) || 0;
    const has538Loan = selectedProperty?.setup?.RD538Loan;
    const unitCount = units?.length || 0;

    const isLayeredProperty =
      selectedProperty?.pap?.filter(
        (pap) => pap.masterAffordableProgram.name !== 'RD',
      ).length > 0;

    // 11, 18, 33, 40
    const allVarianceGt10IncludeComments = [
      {
        variance: '1-0-10-variancePercentage',
        comments: '1-0-10-comments',
      },
      {
        variance: '1-1-6-variancePercentage',
        comments: '1-1-6-comments',
      },
      {
        variance: '1-2-14-variancePercentage',
        comments: '1-2-14-comments',
      },
      {
        variance: '1-3-6-variancePercentage',
        comments: '1-3-6-comments',
      },
    ].every(
      (field) =>
        Math.abs(formValues[field.variance]) < 10 || formValues[field.comments],
    );

    const taxServiceDebtRatio =
      (partILine10ProposedValue -
        (partILine16ProposedValue +
          partILine22ProposedValue +
          partILine23ProposedValue)) /
        (partILine17ProposedValue + partILine25ProposedValue) || 0;
    const isTaxServiceDebtRatioValid = isLayeredProperty
      ? taxServiceDebtRatio >= 1.15 && taxServiceDebtRatio <= 1.3
      : true;

    const otherPartItems = [{ sectionIndex: 0, rowIndexes: [6] }];
    const otherPartIIItems = [
      { sectionIndex: 0, rowIndexes: [9] },
      { sectionIndex: 1, rowIndexes: [5] },
      { sectionIndex: 3, rowIndexes: [5, 2] },
      { sectionIndex: 2, rowIndexes: [13] },
    ];
    const otherPartIIIItems = [{ sectionIndex: 0, rowIndexes: [6] }];
    const otherPartVItems = [
      { sectionIndex: 13, rowIndexes: [0, 1, 2] },
      { sectionIndex: 0, rowIndexes: [4] },
      { sectionIndex: 1, rowIndexes: [4] },
      { sectionIndex: 2, rowIndexes: [2] },
      { sectionIndex: 3, rowIndexes: [2] },
      { sectionIndex: 4, rowIndexes: [1] },
      { sectionIndex: 5, rowIndexes: [2] },
      { sectionIndex: 6, rowIndexes: [5] },
      { sectionIndex: 7, rowIndexes: [1] },
      { sectionIndex: 8, rowIndexes: [6] },
      { sectionIndex: 9, rowIndexes: [3] },
      { sectionIndex: 10, rowIndexes: [5] },
      { sectionIndex: 11, rowIndexes: [1] },
      { sectionIndex: 12, rowIndexes: [2] },
    ];

    const rtoStateResult = calculateRTOComplianceState(
      vacancyRate,
      partILine23ProposedValue,
      partILine23CurrentValue,
      intl,
    );
    const surplusCashStateResult = calculateSurplusComplianceState(
      partILine33ProposedValue,
      partIILine41ProposedValue,
      partIILine40ProposedValue,
    );
    const endingBalanceStateResult = calculateEndingBalanceComplianceState(
      intl,
      partIIILine8ProposedValue,
      partIIIReservedMaxAmount,
      partIIIInsuranceDeductibleAmount,
      unitCount,
      has538Loan,
    );

    return [
      {
        value: intl.formatMessage(rdModalMessages.partI),
        items: [
          {
            value: intl.formatMessage(
              rdModalMessages.complianceItemsPartI8VacancyRateLabel,
              { vacancyRate },
            ),
            state:
              vacancyRate >= 10
                ? COMPLIANCE_STATES.warning
                : COMPLIANCE_STATES.pass,
            tooltip:
              vacancyRate < 10
                ? null
                : intl.formatMessage(
                    rdModalMessages.complianceItemsPartI8VacancyRateTooltip,
                  ),
          },
          {
            value: intl.formatMessage(
              rdModalMessages.complianceItemsPartI23ReturnToOwnerLabel,
              { returnToOwner: formatCurrency(partILine23ProposedValue) },
            ),
            state: rtoStateResult.state,
            tooltip: rtoStateResult.tooltip,
          },
          {
            value: intl.formatMessage(
              rdModalMessages.complianceItemsPartI30NetCashLabel,
              {
                netCash: formatCurrency(netCash, {
                  minimumFractionDigits: 2,
                }),
              },
            ),
            state:
              netCash < 0 ? COMPLIANCE_STATES.warning : COMPLIANCE_STATES.pass,
            tooltip: intl.formatMessage(
              rdModalMessages.complianceItemsPartI30NetCashTooltip,
            ),
          },
          {
            value: intl.formatMessage(
              rdModalMessages.complianceItemsPartISurplusCashLabel,
              {
                surplusCash: formatCurrency(surplusCashStateResult.value, {
                  minimumFractionDigits: 2,
                }),
              },
            ),
            state: surplusCashStateResult.state,
            tooltip: surplusCashStateResult.tooltip,
            tooltipOptions: {
              showForAllStates: true,
            },
          },
          {
            value: intl.formatMessage(
              rdModalMessages.complianceItemsOtherCommentsLabel,
            ),
            state: hasMissingCommentsOnOtherFields({
              formValues,
              partIndex: 0,
              items: otherPartItems,
            })
              ? COMPLIANCE_STATES.warning
              : COMPLIANCE_STATES.pass,
          },
        ],
      },
      {
        value: intl.formatMessage(rdModalMessages.partII),
        items: [
          {
            value: intl.formatMessage(
              rdModalMessages.complianceItemsPartIIVariancesLabel,
            ),
            state: allVarianceGt10IncludeComments
              ? COMPLIANCE_STATES.pass
              : COMPLIANCE_STATES.warning,
            tooltip: intl.formatMessage(
              rdModalMessages.complianceItemsPartIIVariancesTooltip,
            ),
          },
          {
            value: intl.formatMessage(
              rdModalMessages.complianceItemsPartIISubTotalAdministrativeLabel,
              {
                partIILine33ProposedValue: formatCurrency(
                  partIILine33ProposedValue,
                  { minimumFractionDigits: 2 },
                ),
                partILine10ProposedValue: formatCurrency(
                  partILine10ProposedValue,
                  { minimumFractionDigits: 2 },
                ),
                percentage:
                  Math.round(
                    (10000 * partIILine33ProposedValue) /
                      partILine10ProposedValue,
                  ) / 100,
              },
            ),
            state:
              partIILine33ProposedValue / partILine10ProposedValue > 0.23 &&
              !partIILine33Comments
                ? COMPLIANCE_STATES.warning
                : COMPLIANCE_STATES.pass, // eslint-disable-line max-len
            tooltip: intl.formatMessage(
              rdModalMessages.complianceItemsPartIISubTotalAdministrativeTooltip,
            ),
          },
          {
            value: intl.formatMessage(
              rdModalMessages.complianceItemsPartIITaxCreditDebtServiceLabel,
              {
                partILine10ProposedValue: formatCurrency(
                  partILine10ProposedValue,
                  { minimumFractionDigits: 2 },
                ),
                partILine16ProposedValue: formatCurrency(
                  partILine16ProposedValue,
                  { minimumFractionDigits: 2 },
                ),
                partILine22ProposedValue: formatCurrency(
                  partILine22ProposedValue,
                  { minimumFractionDigits: 2 },
                ),
                partILine23ProposedValue: formatCurrency(
                  partILine23ProposedValue,
                  { minimumFractionDigits: 2 },
                ),
                partILine17ProposedValue: formatCurrency(
                  partILine17ProposedValue,
                  { minimumFractionDigits: 2 },
                ),
                partILine25ProposedValue: formatCurrency(
                  partILine25ProposedValue,
                  { minimumFractionDigits: 2 },
                ),
                taxServiceDebtRatio: taxServiceDebtRatio.toFixed(2),
              },
            ),
            state: isTaxServiceDebtRatioValid
              ? COMPLIANCE_STATES.pass
              : COMPLIANCE_STATES.warning, // eslint-disable-line max-len
            tooltip: intl.formatMessage(
              rdModalMessages.complianceItemsPartIITaxCreditDebtServiceTooltip,
            ),
          },
          {
            value: intl.formatMessage(
              rdModalMessages.complianceItemsOtherCommentsLabel,
            ),
            state: hasMissingCommentsOnOtherFields({
              formValues,
              partIndex: 1,
              items: otherPartIIItems,
            })
              ? COMPLIANCE_STATES.warning
              : COMPLIANCE_STATES.pass,
          },
        ],
      },
      {
        value: intl.formatMessage(rdModalMessages.partIII),
        items: [
          {
            value: intl.formatMessage(
              rdModalMessages.complianceItemsPartIBeginningBalanceLabel,
            ),
            state: formValues['2-0-0-comments']
              ? COMPLIANCE_STATES.pass
              : COMPLIANCE_STATES.warning,
            tooltip: intl.formatMessage(
              rdModalMessages.complianceItemsPartIBeginningBalanceTooltip,
            ),
          },
          {
            value: intl.formatMessage(
              rdModalMessages.complianceItemsPartIIIEndingBalanceLabel,
              {
                partIIILine8ProposedValue: formatCurrency(
                  partIIILine8ProposedValue,
                  { minimumFractionDigits: 2 },
                ),
              },
            ),
            state: endingBalanceStateResult.state,
            tooltip: endingBalanceStateResult.tooltip,
          },
          {
            value: intl.formatMessage(
              rdModalMessages.complianceItemsOtherCommentsLabel,
            ),
            state: hasMissingCommentsOnOtherFields({
              formValues,
              partIndex: 2,
              items: otherPartIIIItems,
            })
              ? COMPLIANCE_STATES.warning
              : COMPLIANCE_STATES.pass,
          },
        ],
      },
      {
        value: intl.formatMessage(rdModalMessages.partV),
        items: [
          {
            value: intl.formatMessage(
              rdModalMessages.complianceItemsOtherCommentsLabel,
            ),
            state: hasMissingCommentsOnOtherFields({
              formValues,
              partIndex: 3,
              items: otherPartVItems,
            })
              ? COMPLIANCE_STATES.warning
              : COMPLIANCE_STATES.pass,
          },
        ],
      },
    ];
  }, [dependencyValues, units, skipValidation]);
};

export default useRdBudgetCompliance;
